import { RowRendererProps, Tree, TreeApi, DragPreviewProps, NodeApi } from 'react-arborist';
import { RefObject, isValidElement, cloneElement, useEffect, useState, MouseEvent } from 'react';
import { classNames } from 'primereact/utils';
import { usePersistentRenameInputState, useSectionsTreeResize } from './hooks';
import {
  BundleSectionsTreeCursor,
  BundleSectionsTreeDragPreview,
  BundleSectionsTreeNodeTemplate,
  SuggestionItem,
  SuggestionsMapProps,
} from './components';
import { BundleTreeType, SectionsTreeNode } from './types';
import { getTreeNodesByNodeIds } from './utils';
import { TREE_ROW_HEIGHT } from './constants';

interface OnMoveProps<T> {
  dragNodes: NodeApi<T>[];
  dragIds: string[];
  parentNode: NodeApi<T> | null;
  parentId: string | null;
  index: number;
}

interface OnRenameProps {
  nodeId: string;
  newName: string | SuggestionItem;
  type: BundleTreeType;
}

// Extra drop check from the host app. Return true to reject.
export interface DisableDropArgs<T> {
  parentNode: NodeApi<T>;
  dragNodes: NodeApi<T>[];
  index: number;
}

interface SectionsTreeProps<T> {
  treeApiRef: RefObject<TreeApi<T> | null>;
  sections: T[];
  initialOpenState?: Record<string, boolean> | undefined;
  manuallyHighlightedLightNodeIds?: string[] | null;
  manuallyHighlightedDarkNodeId?: string | null;
  suggestionsMap?: SuggestionsMapProps;
  disableMultiSelection?: boolean;
  paddingTop?: number;
  paddingBottom?: number;
  onActivate?: (node: NodeApi<T>) => void;
  onSelect?: (node: NodeApi<T>[]) => void;
  onMove?: (props: OnMoveProps<T>) => void;
  onRename?: (props: OnRenameProps) => void;
  onContextMenu?: (e: MouseEvent<HTMLElement>, node: NodeApi<SectionsTreeNode>) => void;
  disableDropRule?: (args: DisableDropArgs<T>) => boolean;
}

interface SectionsTreeRowProps {
  rowProps: RowRendererProps<SectionsTreeNode>;
  manuallyHighlightedLightNodeIds?: string[] | null;
  manuallyHighlightedDarkNodeId?: string | null;
  suggestionsMap?: SuggestionsMapProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  persistentInputState: any;
  onContextMenu?: (e: MouseEvent<HTMLElement>, node: NodeApi<SectionsTreeNode>) => void;
}

const SectionsTreeRow: React.FC<SectionsTreeRowProps> = ({
  rowProps,
  manuallyHighlightedLightNodeIds,
  manuallyHighlightedDarkNodeId,
  suggestionsMap,
  persistentInputState,
  onContextMenu,
}) => {
  const { node, innerRef, attrs, children } = rowProps;
  const [isHovered, setIsHovered] = useState(false);

  // Ancestors of the viewed page (shallowest → deepest); only the deepest visible one is highlighted.
  const highlightedPath = manuallyHighlightedLightNodeIds ?? [];
  const pathIndex = highlightedPath.indexOf(node.id);
  const isDeepestVisibleInPath = pathIndex !== -1 && (pathIndex === highlightedPath.length - 1 || !node.isOpen);
  // Shade selected rows only for multi-selection.
  const selectionCount = node.tree.selectedIds.size;
  const isMultiSelected = selectionCount > 1 && node.isSelected;
  const isGreenRow = isDeepestVisibleInPath || node.id === manuallyHighlightedDarkNodeId || isMultiSelected;

  const openContextMenu = (e: MouseEvent<HTMLElement>) => {
    if (onContextMenu && !node.isEditing) {
      onContextMenu(e, node);
      persistentInputState.setNodeId(node.data.id);
      persistentInputState.setValue(node.data.name);
    }
  };

  let childWithExtraProps = children;
  if (isValidElement(children)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    childWithExtraProps = cloneElement(children as React.ReactElement<any>, {
      suggestionsMap,
      persistentInputState,
      rowHighlight: isGreenRow ? 'green' : 'grey',
      isRowHovered: isHovered,
      onEllipsisClick: onContextMenu ? openContextMenu : undefined,
    });
  }

  return (
    <div
      {...attrs}
      ref={innerRef}
      draggable={!node.isEditing}
      onFocus={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        // react-arborist only checks `metaKey` (Cmd) for multi-select; treat Ctrl the same.
        if ((e.metaKey || e.ctrlKey) && !node.tree.props.disableMultiSelection) {
          e.stopPropagation();
          if (node.isSelected) {
            node.deselect();
          } else {
            node.selectMulti();
          }
        } else if (e.shiftKey && !node.tree.props.disableMultiSelection) {
          e.stopPropagation();
          node.selectContiguous();
        } else {
          node.handleClick(e);
        }
      }}
      onContextMenu={(e) => {
        if (onContextMenu && !node.isEditing) {
          e.preventDefault();
          openContextMenu(e);
        }
      }}
      className={classNames(
        'flex w-full cursor-pointer items-center pr-[12px] pl-[24px]',
        'focus-visible:outline-fletchers-green-500',
        {
          // Hover background on the drop target.
          '!bg-fletchers-gray-80': node.willReceiveDrop && !isGreenRow,
          '!bg-fletchers-green-75': node.willReceiveDrop && isGreenRow,
          'bg-fletchers-green-50 hover:!bg-fletchers-green-75': isDeepestVisibleInPath || isMultiSelected,
          'bg-fletchers-green-100 hover:!bg-fletchers-green-75': node.id === manuallyHighlightedDarkNodeId,
          'hover:bg-fletchers-gray-80': !isGreenRow,
        },
      )}
    >
      {childWithExtraProps}
    </div>
  );
};

export const SectionsTree: React.FC<SectionsTreeProps<SectionsTreeNode>> = ({
  treeApiRef,
  sections,
  initialOpenState,
  manuallyHighlightedLightNodeIds,
  manuallyHighlightedDarkNodeId,
  suggestionsMap,
  disableMultiSelection = false,
  paddingTop,
  paddingBottom,
  onActivate,
  onSelect,
  onMove,
  onRename,
  onContextMenu,
  disableDropRule,
}) => {
  const { containerRef, containerHeight, containerWidth, setSizesManually } = useSectionsTreeResize();
  const persistentInputState = usePersistentRenameInputState();

  useEffect(() => {
    if ((containerHeight === 0 || containerWidth === 0) && containerRef && containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setSizesManually(height, width);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className="bg-fletchers-gray-50 h-full max-h-full w-full max-w-full overflow-x-hidden text-nowrap"
    >
      {!!containerHeight && !!containerWidth && !!sections.length && (
        <>
          <Tree
            ref={treeApiRef}
            className="px-0"
            data={sections}
            initialOpenState={initialOpenState}
            openByDefault={false}
            selectionFollowsFocus={false}
            disableMultiSelection={disableMultiSelection}
            width={'100%'}
            height={containerHeight}
            rowHeight={TREE_ROW_HEIGHT}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
            disableDrag={!onMove}
            disableDrop={(args) => {
              // Generic guard: never drop onto a node that's currently in
              // rename mode.
              if (args.dragNodes.some((n) => n.isEditing)) return true;
              // Host app can veto with domain-specific rules (e.g. Providers
              // must stay at the root level).
              return disableDropRule ? disableDropRule(args) : false;
            }}
            renderRow={(props: RowRendererProps<SectionsTreeNode>) => (
              <SectionsTreeRow
                rowProps={props}
                manuallyHighlightedLightNodeIds={manuallyHighlightedLightNodeIds}
                manuallyHighlightedDarkNodeId={manuallyHighlightedDarkNodeId}
                suggestionsMap={suggestionsMap}
                persistentInputState={persistentInputState}
                onContextMenu={onContextMenu}
              />
            )}
            renderDragPreview={({ dragIds, ...props }: DragPreviewProps) => {
              if (persistentInputState.data.nodeId && dragIds.includes(persistentInputState.data.nodeId)) {
                return null;
              }
              const labels = getTreeNodesByNodeIds(sections, dragIds);
              return <BundleSectionsTreeDragPreview {...props} dragIds={dragIds} labels={labels} />;
            }}
            renderCursor={BundleSectionsTreeCursor}
            onActivate={(node) => {
              // Only activate; expand/collapse is on the chevron.
              if (!node.isEditing && onActivate) {
                onActivate(node);
              }
            }}
            onSelect={(nodes) => {
              if (onSelect) {
                onSelect(nodes);
              }
            }}
            onMove={({ dragNodes, dragIds, parentNode, parentId, index }) => {
              if (onMove) {
                onMove({ dragNodes, dragIds, parentNode, parentId, index });
              }
            }}
            onRename={({ id, name, node }) => {
              const newName: SuggestionItem | string = JSON.parse(name);
              if (onRename) {
                onRename({ nodeId: id, newName: newName, type: node.data.type });
              }
            }}
          >
            {BundleSectionsTreeNodeTemplate}
          </Tree>
        </>
      )}
    </div>
  );
};
